// Form-only membership schema for the Access body's hosts — the Edit-access
// drawer, the Add-a-person drawer and the create wizard's Access step. This is
// FRONTEND-ONLY UX validation, never stricter than the backend (CONVENTIONS
// §Schemas): the backend `chk_upr_admin_xor_role` is authoritative; this only
// gates Next/Submit and flags a missing role. Maps onto `AccessBody`'s
// `AccessValues`, and onto the doors' bodies (`membershipBody`).

import { z } from 'zod';
import type { ScopeWrite } from '@ems-app/schemas/users/requests';
import { EMPTY_SCOPE, scopeFormShape, scopeIsEmpty, scopeSectionsOf, type ScopeFormValues } from './scope.js';

/** A users-namespace translator (loose signature so react-i18next's `t` assigns). */
export type Tr = (key: string, opts?: Record<string, unknown>) => string;

/** The membership Zod shape (no defaults → input === output, as useFormShell wants). */
export const membershipShape = {
  is_project_admin: z.boolean(),
  role_id: z.number().int().positive().nullable(),
  scope: scopeFormShape,
};

export interface MembershipFormValues {
  is_project_admin: boolean;
  role_id: number | null;
  scope: ScopeFormValues;
}

/** A new member's starting point: no admin, no role yet, no scope (the ONE default). */
export const EMPTY_MEMBERSHIP: MembershipFormValues = { is_project_admin: false, role_id: null, scope: EMPTY_SCOPE };

// A non-admin membership needs a role; an admin carries none (AccessBody owns the XOR).
// The message is a sentinel — the consumer renders the i18n string when the field
// has an error, so the schema stays static (no resolver churn on language change).
export function refineMembership(v: MembershipFormValues, ctx: z.RefinementCtx): void {
  if (!v.is_project_admin && v.role_id == null) {
    ctx.addIssue({ code: 'custom', path: ['role_id'], message: 'ROLE_REQUIRED' });
  }
}

/** The membership form schema (the drawers). The wizard composes the same shape + refine with its identity fields. */
export const membershipFormSchema = z.object(membershipShape).superRefine(refineMembership);

/** The door's body from the form (ADR-063 · ADR-050 §2): the membership, and — for a non-admin — the scope block.
 *  Left to the host, the block rides when something is granted (a new member starts with no scope: nothing to write,
 *  nothing to say); the editor says it (`withScope`): the block rides when the scope part CHANGED — an emptied scope
 *  is a write, a role-only change leaves the scope alone (a giver changes the role of a person whose scope they may
 *  not edit). */
export function membershipBody(
  v: MembershipFormValues,
  opts: { withScope?: boolean; confirmUnwind?: boolean } = {},
): { is_project_admin: boolean; role_id: number | null; scope?: ScopeWrite } {
  if (v.is_project_admin) return { is_project_admin: true, role_id: null };
  const withScope = opts.withScope ?? !scopeIsEmpty(v.scope);
  if (!withScope) return { is_project_admin: false, role_id: v.role_id };
  return { is_project_admin: false, role_id: v.role_id, scope: { ...scopeSectionsOf(v.scope), confirm_unwind: opts.confirmUnwind ?? false } };
}
